/**
 * Iter 69 — Master Lists editor for ERP Configuration page.
 *
 * Exposes every key under /api/config-masters as an editable list grouped by
 * domain. Each list supports: add value, remove value, rename (inline).
 *
 * Backend endpoints (already exist):
 *   GET  /api/config-masters             → { masters: { key: [v, ...], ... } }
 *   PUT  /api/config-masters/{key}       → { values: [...] }
 */
import React, { useEffect, useState } from 'react';
import { Plus, X, Pencil, Check, Loader2, Save } from 'lucide-react';
import { apiGet, apiPut } from '../lib/api';
import { toast } from 'sonner';

// Group the master keys into logical sections so the UI is scannable.
const GROUPS = [
  {
    title: 'Organization',
    color: 'slate',
    keys: [
      ['departments', 'Departments', 'Used everywhere — Employee form, approval routing, email From-map, sidebar grouping.'],
      ['employee_grades', 'Employee Grades', 'M1/M2/M3/S1/S2/W1 grades — drives the role × grade access-control matrix.'],
      ['employee_designations', 'Designations', 'Job titles shown in the Employee Master form.'],
    ],
  },
  {
    title: 'CRM — Contacts',
    color: 'cyan',
    keys: [
      ['contact_designations', 'Contact Designations', 'Job titles for client-side contacts (MD, Director, Manager…).'],
      ['contact_relationship_types', 'Relationship Types', 'Decision-Maker, Influencer, Gatekeeper, etc.'],
      ['contact_communication_channels', 'Communication Channels', 'Email, Phone, WhatsApp, Site Visit…'],
      ['contact_tags', 'Contact Tags', 'Hot, Warm, VIP, Strategic, Do-Not-Call…'],
    ],
  },
  {
    title: 'Labour Master',
    color: 'amber',
    keys: [
      ['labour_trades', 'Trades / Skills', 'Mason, Carpenter, Welder, Bar-Bender, etc.'],
      ['labour_designations', 'Labour Designations', 'Skill levels (Skilled / Semi-Skilled / Unskilled / Supervisor).'],
      ['labour_genders', 'Gender Options', 'Used in the labour roster.'],
    ],
  },
  {
    title: 'HR — Attendance',
    color: 'emerald',
    keys: [
      ['attendance_status_codes', 'Attendance Status Codes', 'Short codes used in the roster (P, A, H, WFH, WO, SL…).'],
      ['attendance_leave_categories', 'Leave Categories', 'Categories shown in the leave application form.'],
    ],
  },
];

const COLOR_CLS = {
  slate: { bg: 'bg-slate-50', border: 'border-slate-300', heading: 'text-slate-700' },
  indigo: { bg: 'bg-teal-50/40', border: 'border-teal-200', heading: 'text-[#1F7A6E]' },
  cyan: { bg: 'bg-cyan-50/40', border: 'border-cyan-200', heading: 'text-cyan-800' },
  amber: { bg: 'bg-amber-50/40', border: 'border-amber-200', heading: 'text-amber-800' },
  emerald: { bg: 'bg-emerald-50/40', border: 'border-emerald-200', heading: 'text-emerald-800' },
  rose: { bg: 'bg-rose-50/40', border: 'border-rose-200', heading: 'text-rose-800' },
};

export default function MasterListsEditor() {
  const [masters, setMasters] = useState({}); // { key: [values] }
  const [dirty, setDirty] = useState({});     // { key: true } if changed since save
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/config-masters');
      setMasters(r.masters || {});
      setDirty({});
    } catch {
      toast.error('Failed to load master lists');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateOne = (key, values) => {
    setMasters((m) => ({ ...m, [key]: values }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const saveOne = async (key) => {
    setSavingKey(key);
    try {
      await apiPut(`/config-masters/${key}`, { values: masters[key] || [] });
      toast.success(`${key.replace(/_/g, ' ')} saved`);
      setDirty((d) => { const { [key]: _, ...rest } = d; return rest; });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Save failed';
      if (e?.response?.status === 409) {
        // Backend blocked deletion because items have live data
        window.alert(detail);
        // Reload to restore the removed items
        await load();
      } else {
        toast.error(detail);
      }
    } finally { setSavingKey(null); }
  };

  const saveAll = async () => {
    const keys = Object.keys(dirty);
    if (keys.length === 0) { toast.info('No changes to save'); return; }
    for (const key of keys) {
      try {
        await apiPut(`/config-masters/${key}`, { values: masters[key] || [] });
      } catch (e) {
        const detail = e?.response?.data?.detail || '';
        if (e?.response?.status === 409) {
          window.alert(detail);
          await load(); return;
        }
        toast.error(`Failed to save ${key}: ${detail}`);
        return;
      }
    }
    toast.success(`Saved ${keys.length} master list${keys.length === 1 ? '' : 's'}`);
    setDirty({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading master lists…
      </div>
    );
  }

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-6" data-testid="master-lists-editor">
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-display font-bold text-[#0F172A]">Master Lists</div>
          <p className="text-xs text-slate-600 mt-0.5">
            These dropdowns/tags appear everywhere in the ERP — CRM forms, labour master, attendance, SVR.
            Edit a list here and every screen using it updates immediately.
          </p>
        </div>
        <button
          onClick={saveAll}
          disabled={dirtyCount === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="master-save-all"
        >
          <Save size={13} /> {dirtyCount > 0 ? `Save All (${dirtyCount})` : 'No Changes'}
        </button>
      </div>

      {GROUPS.map((g) => (
        <section key={g.title} className={`rounded-sm border ${COLOR_CLS[g.color].border} ${COLOR_CLS[g.color].bg} p-4 space-y-3`}>
          <h3 className={`text-xs font-display font-bold uppercase tracking-widest ${COLOR_CLS[g.color].heading}`}>{g.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.keys.map(([key, label, hint]) => (
              <MasterListCard
                key={key}
                k={key}
                label={label}
                hint={hint}
                values={masters[key] || []}
                onChange={(vals) => updateOne(key, vals)}
                onSave={() => saveOne(key)}
                isDirty={!!dirty[key]}
                isSaving={savingKey === key}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function MasterListCard({ k, label, hint, values, onChange, onSave, isDirty, isSaving }) {
  const [newVal, setNewVal] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState('');

  const add = () => {
    const v = newVal.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast.error('Value already exists');
      return;
    }
    onChange([...values, v]);
    setNewVal('');
  };

  const removeAt = (i) => {
    const v = values[i];
    if (!window.confirm(`Remove "${v}" from ${label}?`)) return;
    onChange(values.filter((_, idx) => idx !== i));
  };

  const commitRename = (i) => {
    const v = editVal.trim();
    if (!v) { toast.error('Value cannot be empty'); return; }
    if (values.some((x, idx) => idx !== i && x.toLowerCase() === v.toLowerCase())) {
      toast.error('Duplicate value'); return;
    }
    const next = values.slice();
    next[i] = v;
    onChange(next);
    setEditIdx(null);
    setEditVal('');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`master-card-${k}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-display font-bold text-[#0F172A] truncate">{label}</div>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{hint}</p>
        </div>
        <div className="flex items-center gap-2 ml-2 shrink-0">
          <span className="text-[10px] font-mono text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded">{values.length}</span>
          {isDirty && (
            <button
              onClick={onSave}
              disabled={isSaving}
              className="text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
              data-testid={`master-save-${k}`}
            >
              {isSaving ? '…' : '✓ Save'}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-1 max-h-48 overflow-y-auto pr-1 mb-2" data-testid={`master-list-${k}`}>
        {values.length === 0 && (
          <p className="text-[11px] text-slate-400 italic px-1 py-2">No values yet — add one below.</p>
        )}
        {values.map((v, i) => (
          <div key={`${v}-${i}`} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 group">
            {editIdx === i ? (
              <input
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename(i);
                  if (e.key === 'Escape') { setEditIdx(null); setEditVal(''); }
                }}
                autoFocus
                className="flex-1 min-w-0 px-1.5 py-0.5 border border-[#1F7A6E] rounded-sm text-xs focus:outline-none"
                data-testid={`master-rename-input-${k}-${i}`}
              />
            ) : (
              <span className="flex-1 min-w-0 truncate">{v}</span>
            )}
            {editIdx === i ? (
              <button onClick={() => commitRename(i)} className="text-emerald-600 hover:text-emerald-800"
                data-testid={`master-rename-save-${k}-${i}`}>
                <Check size={12} />
              </button>
            ) : (
              <button
                onClick={() => { setEditIdx(i); setEditVal(v); }}
                className="text-slate-300 hover:text-[#1F7A6E] opacity-0 group-hover:opacity-100 transition-opacity"
                title="Rename"
                data-testid={`master-rename-${k}-${i}`}
              >
                <Pencil size={11} />
              </button>
            )}
            <button
              onClick={() => removeAt(i)}
              className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove"
              data-testid={`master-remove-${k}-${i}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1">
        <input
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={`Add a ${label.toLowerCase()}…`}
          className="flex-1 min-w-0 px-2 py-1 border border-slate-300 rounded-sm text-xs focus:outline-none focus:border-[#1F7A6E]"
          data-testid={`master-add-input-${k}`}
        />
        <button
          onClick={add}
          disabled={!newVal.trim()}
          className="px-2 py-1 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-[10px] font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          data-testid={`master-add-${k}`}
        >
          <Plus size={11} /> Add
        </button>
      </div>
    </div>
  );
}
